<script setup lang="ts">
  import { ref, watch, nextTick } from 'vue';
  import { useDebounceFn } from '@vueuse/core';
  import SearchIcon from '@/shared/assets/icons/SearchIcon.svg';
  import { IconImage, SelectInput } from '@/shared/ui';
  import {
    HOME_SEARCH_TABS,
    HOME_SEARCH_CRITERIA_OPTIONS,
    HOME_SEARCH_DEFAULTS,
  } from '@/pages/home/model';
  import type { HomeSearchCriteria, HomeSearchItemType } from '@/pages/home/model';

  const emit = defineEmits<{
    (e: 'search', term: string, criteria: HomeSearchCriteria, itemType: HomeSearchItemType): void;
    (e: 'clear'): void;
  }>();

  const activeTab = ref<HomeSearchItemType>(HOME_SEARCH_DEFAULTS.itemType);
  const selectedCriteria = ref<HomeSearchCriteria>(HOME_SEARCH_DEFAULTS.criteria);
  const searchTerm = ref('');
  const searchInputRef = ref<HTMLInputElement | null>(null);
  let debounceVersion = 0;

  function cancelPendingDebouncedSearch() {
    debounceVersion += 1;
  }

  function emitSearch(term: string) {
    if (!term.trim()) return;
    emit('search', term.trim(), selectedCriteria.value, activeTab.value);
  }

  const debouncedEmitSearch = useDebounceFn((term: string, version: number) => {
    if (version !== debounceVersion) return;
    emitSearch(term);
  }, HOME_SEARCH_DEFAULTS.debounceMs);

  watch(searchTerm, (newVal) => {
    if (!newVal.trim()) {
      cancelPendingDebouncedSearch();
      emit('clear');
      return;
    }
    debouncedEmitSearch(newVal, debounceVersion);
  });

  function selectTab(tabId: HomeSearchItemType) {
    if (activeTab.value === tabId) return;
    cancelPendingDebouncedSearch();
    activeTab.value = tabId;
    emitSearch(searchTerm.value);
  }

  function handleCriteriaChange(value: string | number) {
    if (value !== 'prefix' && value !== 'name') return;
    cancelPendingDebouncedSearch();
    selectedCriteria.value = value;
    emitSearch(searchTerm.value);
    nextTick(() => searchInputRef.value?.focus());
  }
</script>

<template>
  <div class="flex flex-col gap-2 border border-gray-02 rounded-2xl p-4">
    <div class="flex bg-gray-02 rounded-lg max-w-fit p-0.5 gap-0.5">
      <button
        v-for="tab in HOME_SEARCH_TABS"
        :key="tab.id"
        type="button"
        class="px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-150 cursor-pointer"
        :class="
          activeTab === tab.id
            ? 'bg-white-01 shadow-sm text-gray-07'
            : 'text-gray-03 hover:text-gray-07'
        "
        :data-testid="`tab-${tab.id}`"
        @click="selectTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="flex flex-row items-center bg-white-01 border border-gray-02 rounded-lg h-[42px]">
      <div class="flex-none w-[140px] h-full border-r border-gray-02">
        <SelectInput
          :options="HOME_SEARCH_CRITERIA_OPTIONS"
          :model-value="selectedCriteria"
          container-class="h-full"
          button-class="!border-none !rounded-none !rounded-l-lg h-full py-2 px-4"
          arrow-right="right-4"
          data-testid="criteria-select"
          @update:model-value="handleCriteriaChange"
        />
      </div>

      <div class="flex-1 flex items-center gap-3 pl-4 pr-4">
        <IconImage
          :src="SearchIcon"
          alt="Buscar"
          class="w-5 h-5 flex-none text-gray-02"
        />
        <input
          id="home-search-term"
          ref="searchInputRef"
          v-model="searchTerm"
          type="text"
          placeholder="Buscar"
          data-testid="search-input"
          class="flex-1 text-sm font-normal text-gray-07 placeholder:text-gray-03 focus:outline-none bg-transparent"
        >
      </div>
    </div>
  </div>
</template>
